import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Slider from '@mui/material/Slider';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Switch from '@mui/material/Switch';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BuildIcon from '@mui/icons-material/Build';
import FlightIcon from '@mui/icons-material/Flight';
import TrainIcon from '@mui/icons-material/Train';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RamenDiningIcon from '@mui/icons-material/RamenDining';
import LocalPizzaIcon from '@mui/icons-material/LocalPizza';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import BakeryDiningIcon from '@mui/icons-material/BakeryDining';
import IcecreamIcon from '@mui/icons-material/Icecream';
import EmojiFoodBeverageIcon from '@mui/icons-material/EmojiFoodBeverage';
import TapasIcon from '@mui/icons-material/Tapas';
import DinnerDiningIcon from '@mui/icons-material/DinnerDining';
import HikingIcon from '@mui/icons-material/Hiking';
import MuseumIcon from '@mui/icons-material/Museum';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import NightlifeIcon from '@mui/icons-material/Nightlife';
import SportsIcon from '@mui/icons-material/Sports';
import ParkIcon from '@mui/icons-material/Park';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import StarIcon from '@mui/icons-material/Star';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isBefore,
  isAfter,
  addMonths,
  subMonths
} from 'date-fns';

interface Airport {
  id: string;
  name: string;
}

interface TripPreferences {
  country_id: number | null;
  city_ids: number[];
  duration_in_days: number | null;
  budget: number | null;
  total_travelers: number | null;
  cuisine_type: string[];
  activity_type: string[];
  name: string;
  origin_airport: string;
  departure_airport: string;
  travel_mode: string;
  arrival_date: string;
  end_date: string;
  preserve_city_order: boolean;
}

interface PreferencesLookup {
  activity_types: string[];
  cuisine_types: string[];
  travel_modes: string[];
  'trip-statuses': string[];
}

interface RebuildTripDialogProps {
  open: boolean;
  preferences: TripPreferences;
  preferencesLookup: PreferencesLookup;
  airports: Airport[];
  loading: boolean;
  onClose: () => void;
  onRebuild: (preferences: TripPreferences) => void;
}

function RebuildTripDialog({
  open,
  preferences,
  preferencesLookup,
  airports,
  loading,
  onClose,
  onRebuild,
}: RebuildTripDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Local state for the form
  const [tripPreferences, setTripPreferences] = useState<TripPreferences>(preferences);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [durationStartDate, setDurationStartDate] = useState<Date | null>(null);
  const [durationEndDate, setDurationEndDate] = useState<Date | null>(null);
  const [isSelectingEndDate, setIsSelectingEndDate] = useState<boolean>(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTripPreferences(preferences);
      setDurationStartDate(null);
      setDurationEndDate(null);
      setIsSelectingEndDate(false);
      setCurrentMonth(new Date());
    }
  }, [open, preferences]);

  const handleRebuild = () => {
    onRebuild(tripPreferences);
  };

  const getTravelModeIcon = (mode: string) => {
    const modeLower = mode.toLowerCase();
    if (modeLower.includes('plane') || modeLower.includes('flight') || modeLower.includes('air')) {
      return <FlightIcon />;
    } else if (modeLower.includes('train') || modeLower.includes('rail')) {
      return <TrainIcon />;
    } else if (modeLower.includes('bus')) {
      return <DirectionsBusIcon />;
    } else if (modeLower.includes('boat') || modeLower.includes('ferry') || modeLower.includes('cruise')) {
      return <DirectionsBoatIcon />;
    } else if (modeLower.includes('car') || modeLower.includes('drive')) {
      return <DirectionsCarIcon />;
    }
    return <DirectionsCarIcon />;
  };

  const getCuisineIcon = (cuisine: string) => {
    const cuisineLower = cuisine.toLowerCase();
    if (cuisineLower.includes('italian') || cuisineLower.includes('pizza')) {
      return <LocalPizzaIcon fontSize="small" />;
    } else if (cuisineLower.includes('asian') || cuisineLower.includes('chinese') || cuisineLower.includes('japanese') || cuisineLower.includes('ramen') || cuisineLower.includes('thai') || cuisineLower.includes('vietnamese')) {
      return <RamenDiningIcon fontSize="small" />;
    } else if (cuisineLower.includes('french') || cuisineLower.includes('pastry') || cuisineLower.includes('bakery')) {
      return <BakeryDiningIcon fontSize="small" />;
    } else if (cuisineLower.includes('american') || cuisineLower.includes('burger')) {
      return <LunchDiningIcon fontSize="small" />;
    } else if (cuisineLower.includes('dessert') || cuisineLower.includes('sweet')) {
      return <IcecreamIcon fontSize="small" />;
    } else if (cuisineLower.includes('spanish') || cuisineLower.includes('tapas') || cuisineLower.includes('mediterranean')) {
      return <TapasIcon fontSize="small" />;
    } else if (cuisineLower.includes('mexican') || cuisineLower.includes('indian') || cuisineLower.includes('middle eastern')) {
      return <DinnerDiningIcon fontSize="small" />;
    } else if (cuisineLower.includes('cafe') || cuisineLower.includes('coffee')) {
      return <EmojiFoodBeverageIcon fontSize="small" />;
    }
    return <RestaurantIcon fontSize="small" />;
  };

  const getActivityIcon = (activity: string) => {
    const activityLower = activity.toLowerCase();
    if (activityLower.includes('hiking') || activityLower.includes('adventure') || activityLower.includes('trek')) {
      return <HikingIcon fontSize="small" />;
    } else if (activityLower.includes('museum') || activityLower.includes('culture') || activityLower.includes('art')) {
      return <MuseumIcon fontSize="small" />;
    } else if (activityLower.includes('beach') || activityLower.includes('swim')) {
      return <BeachAccessIcon fontSize="small" />;
    } else if (activityLower.includes('shop')) {
      return <ShoppingBagIcon fontSize="small" />;
    } else if (activityLower.includes('nightlife') || activityLower.includes('bar') || activityLower.includes('club')) {
      return <NightlifeIcon fontSize="small" />;
    } else if (activityLower.includes('sport')) {
      return <SportsIcon fontSize="small" />;
    } else if (activityLower.includes('nature') || activityLower.includes('park') || activityLower.includes('wildlife')) {
      return <ParkIcon fontSize="small" />;
    } else if (activityLower.includes('theater') || activityLower.includes('show') || activityLower.includes('entertainment')) {
      return <TheaterComedyIcon fontSize="small" />;
    } else if (activityLower.includes('photo') || activityLower.includes('sightsee')) {
      return <CameraAltIcon fontSize="small" />;
    }
    return <StarIcon fontSize="small" />;
  };

  const renderCalendar = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    const firstDayOfWeek = start.getDay();
    const paddingStart = Array(firstDayOfWeek).fill(null);
    const paddingEnd = Array(6 - end.getDay()).fill(null);
    const allDays = [...paddingStart, ...days, ...paddingEnd];

    return allDays.map((day, index) => {
      if (!day) return <Box key={index} sx={{ height: 36 }} />;

      const isStart = durationStartDate && isSameDay(day, durationStartDate);
      const isEnd = durationEndDate && isSameDay(day, durationEndDate);
      const isInRange = durationStartDate && durationEndDate &&
                       (isSameDay(day, durationStartDate) || isSameDay(day, durationEndDate) ||
                        (isAfter(day, durationStartDate) && isBefore(day, durationEndDate)));

      return (
        <Button
          key={index}
          variant={isStart || isEnd ? "contained" : isInRange ? "outlined" : "text"}
          color={isStart ? "primary" : isEnd ? "secondary" : "primary"}
          onClick={() => {
            if (!isSelectingEndDate) {
              setDurationStartDate(day);
              setDurationEndDate(null);
              setIsSelectingEndDate(true);
              setTripPreferences({ ...tripPreferences, duration_in_days: 1 });
            } else {
              if (durationStartDate && isBefore(day, durationStartDate)) {
                setDurationStartDate(day);
                setDurationEndDate(durationStartDate);
                const diffTime = Math.abs(durationStartDate.getTime() - day.getTime());
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                setTripPreferences({ ...tripPreferences, duration_in_days: diffDays });
              } else {
                setDurationEndDate(day);
                const diffTime = Math.abs(day.getTime() - durationStartDate!.getTime());
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                setTripPreferences({ ...tripPreferences, duration_in_days: diffDays });
              }
              setIsSelectingEndDate(false);
            }
          }}
          sx={{
            minWidth: 36,
            height: 36,
            p: 0,
            fontSize: '0.875rem',
            ...(isInRange && !isStart && !isEnd && {
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
            })
          }}
        >
          {format(day, 'd')}
        </Button>
      );
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>Rebuild Trip</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="warning.main" sx={{ mb: 2, fontWeight: 500 }}>
          ⚠️ Warning: This will replace your existing trip with a new itinerary based on the preferences below.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Modify the preferences to rebuild your trip. This will generate a fresh itinerary with new segments.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Duration Calendar */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Trip Duration
            </Typography>
            <Paper
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.100' }
              }}
              onClick={() => {
                const newMonth = durationStartDate || new Date();
                setCurrentMonth(newMonth);
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarTodayIcon color="primary" fontSize="small" />
                <Typography variant="body2">
                  {!durationStartDate && !durationEndDate ? 'Select travel dates' :
                   durationStartDate && !durationEndDate ? `${format(durationStartDate, 'MMM dd, yyyy')} - Select end date` :
                   durationStartDate && durationEndDate ? `${format(durationStartDate, 'MMM dd, yyyy')} - ${format(durationEndDate, 'MMM dd, yyyy')} (${tripPreferences.duration_in_days || 0} days)` : ''}
                </Typography>
              </Box>
            </Paper>

            {/* Calendar */}
            <Paper sx={{ p: 2, mt: 1 }}>
              {/* Calendar Header */}
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <IconButton onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} size="small">
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="subtitle1" fontWeight="bold">
                  {format(currentMonth, 'MMMM yyyy')}
                </Typography>
                <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} size="small">
                  <ChevronRightIcon />
                </IconButton>
              </Box>

              {/* Day Headers */}
              <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0.5} mb={1}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <Typography key={day} variant="caption" align="center" color="text.secondary">
                    {day}
                  </Typography>
                ))}
              </Box>

              {/* Calendar Days */}
              <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0.5}>
                {renderCalendar()}
              </Box>

              {durationStartDate && !durationEndDate && (
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  Now select your end date
                </Typography>
              )}
            </Paper>
          </Box>

          {/* Budget */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Budget: ${(tripPreferences.budget || 100).toLocaleString()}
            </Typography>
            <Slider
              value={tripPreferences.budget || 100}
              onChange={(e, newValue) => setTripPreferences({ ...tripPreferences, budget: newValue as number })}
              min={100}
              max={50000}
              step={100}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `$${value.toLocaleString()}`}
              marks={[
                { value: 100, label: '$100' },
                { value: 10000, label: '$10K' },
                { value: 25000, label: '$25K' },
                { value: 50000, label: '$50K' }
              ]}
              sx={{
                '& .MuiSlider-valueLabel': {
                  fontSize: 12,
                  fontWeight: 'normal',
                  top: -6,
                  backgroundColor: 'unset',
                  color: 'text.primary',
                  '&:before': {
                    display: 'none'
                  }
                }
              }}
            />
          </Box>

          {/* Total Travelers */}
          <FormControl fullWidth>
            <InputLabel id="travelers-label">Number of Travelers</InputLabel>
            <Select
              labelId="travelers-label"
              label="Number of Travelers"
              value={tripPreferences.total_travelers || ''}
              onChange={(e) => setTripPreferences({ ...tripPreferences, total_travelers: e.target.value as number })}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                <MenuItem key={num} value={num}>
                  {num}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Origin Airport */}
          <Autocomplete
            options={airports}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              if (option.name && option.id) {
                return `${option.name} (${option.id})`;
              }
              return option.id || option.name || '';
            }}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography variant="body2">
                  {option.name && option.id ? `${option.name} (${option.id})` : (option.name || option.id)}
                </Typography>
              </li>
            )}
            value={airports.find(a => a.id === tripPreferences.origin_airport) || null}
            onChange={(e, newValue) => {
              const airportCode = typeof newValue === 'string' ? newValue : (newValue?.id || '');
              setTripPreferences({ ...tripPreferences, origin_airport: airportCode });
            }}
            isOptionEqualToValue={(option, value) => {
              if (!value) return false;
              return option.id === (typeof value === 'string' ? value : value.id);
            }}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                label="Origin Airport"
                placeholder="e.g., JFK"
              />
            )}
          />

          {/* Departure Airport */}
          <Autocomplete
            options={airports}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              if (option.name && option.id) {
                return `${option.name} (${option.id})`;
              }
              return option.id || option.name || '';
            }}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography variant="body2">
                  {option.name && option.id ? `${option.name} (${option.id})` : (option.name || option.id)}
                </Typography>
              </li>
            )}
            value={airports.find(a => a.id === tripPreferences.departure_airport) || null}
            onChange={(e, newValue) => {
              const airportCode = typeof newValue === 'string' ? newValue : (newValue?.id || '');
              setTripPreferences({ ...tripPreferences, departure_airport: airportCode });
            }}
            isOptionEqualToValue={(option, value) => {
              if (!value) return false;
              return option.id === (typeof value === 'string' ? value : value.id);
            }}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                label="Departure Airport"
                placeholder="e.g., LIS"
              />
            )}
          />

          {/* Travel Mode */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Travel Mode
            </Typography>
            <ToggleButtonGroup
              value={tripPreferences.travel_mode || ''}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) {
                  setTripPreferences({ ...tripPreferences, travel_mode: newValue });
                }
              }}
              fullWidth
              sx={{
                display: 'flex',
                gap: 1,
                '& .MuiToggleButton-root': {
                  flex: 1,
                  textTransform: 'capitalize',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  py: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    borderColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    }
                  }
                }
              }}
            >
              {(preferencesLookup.travel_modes || []).map((mode) => (
                <ToggleButton key={mode} value={mode}>
                  {getTravelModeIcon(mode)}
                  <Typography variant="caption">{mode}</Typography>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Preserve City Order */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={tripPreferences.preserve_city_order || false}
                  onChange={(_, checked) => setTripPreferences({ ...tripPreferences, preserve_city_order: checked })}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Preserve city order
                </Typography>
              }
            />
          </Box>

          {/* Cuisine Type */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Cuisine Types
            </Typography>
            <FormGroup sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 1
            }}>
              {(preferencesLookup.cuisine_types || []).map((cuisine) => (
                <FormControlLabel
                  key={cuisine}
                  control={
                    <Switch
                      checked={(tripPreferences.cuisine_type || []).includes(cuisine)}
                      onChange={(e) => {
                        const currentTypes = tripPreferences.cuisine_type || [];
                        const newTypes = e.target.checked
                          ? [...currentTypes, cuisine]
                          : currentTypes.filter(c => c !== cuisine);
                        setTripPreferences({ ...tripPreferences, cuisine_type: newTypes });
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getCuisineIcon(cuisine)}
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {cuisine}
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0 }}
                />
              ))}
            </FormGroup>
          </Box>

          {/* Activity Type */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Activity Types
            </Typography>
            <FormGroup sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 1
            }}>
              {(preferencesLookup.activity_types || []).map((activity) => (
                <FormControlLabel
                  key={activity}
                  control={
                    <Switch
                      checked={(tripPreferences.activity_type || []).includes(activity)}
                      onChange={(e) => {
                        const currentTypes = tripPreferences.activity_type || [];
                        const newTypes = e.target.checked
                          ? [...currentTypes, activity]
                          : currentTypes.filter(a => a !== activity);
                        setTripPreferences({ ...tripPreferences, activity_type: newTypes });
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getActivityIcon(activity)}
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {activity}
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0 }}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleRebuild}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <BuildIcon />}
        >
          {loading ? 'Rebuilding...' : 'Rebuild Trip'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RebuildTripDialog;
